import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { ChevronRightIcon, Info } from 'components/icons';
import PageHeader from 'components/PageHeader';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useVisibleAdminGroups } from './adminItems';

const READ_TENANTS = ['READ.TENANTS'];

/** `/admin` in the new design: one card per group with the pages the user may open in it. */
function AdminHome() {
    const groups = useVisibleAdminGroups();
    const canReadTenant = useHasPermissions(READ_TENANTS);
    const { data: tenant } = useGetCurrentTenantQuery(undefined, { skip: !canReadTenant });

    return (
        <PaddedLayout>
            <div className="flex flex-col gap-[22px] pb-6">
                <div className="flex flex-col gap-1.5">
                    <PageHeader titleId="menu.admin" showBreadcrumb />
                    {tenant?.name ? (
                        <p className="text-grey-600">{t('admin.subtitle', { tenant: tenant.name })}</p>
                    ) : null}
                </div>
                <p className="border-primary-wash-border bg-primary-wash text-primary-ink flex items-center gap-2.5 rounded-lg border px-4 py-3">
                    <Info aria-hidden className="flex-none text-base" />
                    <span>
                        <FormattedMessage
                            id="admin.people-notice"
                            values={{ b: (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong> }}
                        />
                    </span>
                </p>
                <div className="grid grid-cols-1 gap-(--card-gap) md:grid-cols-2 lg:grid-cols-3">
                    {groups.map((group) => (
                        <section
                            key={group.id}
                            aria-labelledby={`admin-card-${group.id}`}
                            className="bg-card border-border new:border-0 new:shadow-card new:outline-hidden flex flex-col gap-2.5 rounded-lg border px-5 py-[18px]"
                        >
                            <h3 id={`admin-card-${group.id}`} className="text-section text-foreground">
                                {t(group.labelId)}
                            </h3>
                            <p className="text-meta text-grey-600 leading-[1.45]">{t(group.descriptionId)}</p>
                            <ul className="mt-0.5 flex flex-col gap-0.5">
                                {group.items.map((item) => (
                                    <li key={item.id} className="border-divider-soft border-t">
                                        <Link
                                            to={item.path}
                                            className="text-foreground hover:text-primary focus-visible:ring-ring/50 flex min-h-[34px] items-center justify-between gap-2 rounded-sm no-underline outline-none focus-visible:ring-3"
                                        >
                                            {t(item.labelId)}
                                            <ChevronRightIcon
                                                aria-hidden
                                                className="text-grey-500 flex-none text-[14px]"
                                            />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </div>
        </PaddedLayout>
    );
}

export default AdminHome;
